package fr.arcplex.network.reporting

import fr.arcplex.network.cache.*
import fr.arcplex.network.http.Serializer
import fr.arcplex.network.logging.Logger

import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject

/**
 * Kinds of reportable facts. Only [VIDEO_STARTED], [VIDEO_STOPPED] and [VIDEO_SEEK] are
 * produced today; [TELEMETRY] and [ERROR] are placeholders for the Coherence telemetry
 * endpoint, which has no contract yet. Events are stored with their free-form [ReportEvent.payload]
 * so a new kind never requires a change to the queue.
 */
enum class ReportEventType { VIDEO_STARTED, VIDEO_PAUSE, VIDEO_SEEK, VIDEO_STOPPED, TELEMETRY, ERROR }

data class ReportEvent(
    val id: String,
    val type: ReportEventType,
    val payload: Map<String, String> = emptyMap(),
    val createdAtMs: Long = 0L,
    val attempts: Int = 0
)

/**
 * Durable FIFO of events waiting to be reported.
 *
 * Events survive a process restart, which is the whole point: an event produced while the
 * device is offline has to be collected and replayed later. Ordering is preserved both in
 * memory and on disk — entries are keyed by a zero-padded monotonic sequence so that a
 * lexical `listKeys()` sort equals chronological order.
 *
 * The queue deliberately does NOT share a [CacheStorage] with [ResponseCache]: that class
 * sweeps every key of the storage it owns and deletes anything that does not parse as one of
 * its own entries, and its entries expire on the API TTL. Both would destroy queued events.
 */
class EventQueue(
    private val storage: CacheStorage = createCacheStorage("report-queue", StorageKind.DURABLE),
    private val maxEvents: Int = DEFAULT_MAX_EVENTS,
    private val maxAgeMs: Long = DEFAULT_MAX_AGE_MS,
    private val now: () -> Long = { currentTimeMillis() }
) {
    companion object {
        const val DEFAULT_MAX_EVENTS = 200
        const val DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000L
        private const val KEY_PREFIX = "evt:"
        private const val SEQ_DIGITS = 12
    }

    private data class Entry(val seq: Long, val event: ReportEvent)

    private val mutex = Mutex()
    private val entries = ArrayDeque<Entry>()
    private var nextSeq = 1L
    private var loaded = false

    /** Reads whatever is already persisted. Called lazily by every operation. */
    suspend fun load() = mutex.withLock { loadLocked() }

    suspend fun size(): Int = mutex.withLock {
        loadLocked()
        trimLocked()
        entries.size
    }

    /** Oldest events first, at most [max]. */
    suspend fun peekBatch(max: Int = Int.MAX_VALUE): List<ReportEvent> = mutex.withLock {
        loadLocked()
        trimLocked()
        entries.take(max).map { it.event }
    }

    suspend fun enqueue(type: ReportEventType, payload: Map<String, String> = emptyMap()): ReportEvent =
        mutex.withLock {
            loadLocked()
            val createdAtMs = now()
            val seq = nextSeq++
            val event = ReportEvent(
                id = "$createdAtMs-$seq",
                type = type,
                payload = payload,
                createdAtMs = createdAtMs
            )
            // Persist before exposing the event: a storage failure degrades to an
            // in-memory-only queue (still reportable now) rather than losing the event.
            writeQuietly(seq, event)
            entries.addLast(Entry(seq, event))
            trimLocked()
            event
        }

    /** The event reached the backend and must not be sent again. */
    suspend fun ack(id: String) = mutex.withLock {
        loadLocked()
        val index = entries.indexOfFirst { it.event.id == id }
        if (index < 0) return@withLock
        val entry = entries.removeAt(index)
        deleteQuietly(keyFor(entry.seq))
    }

    /**
     * A send attempt failed. [permanent] drops the event (the backend will never accept it);
     * otherwise the attempt counter is bumped so the sender can give up after too many tries.
     */
    suspend fun fail(id: String, permanent: Boolean = false) = mutex.withLock {
        loadLocked()
        val index = entries.indexOfFirst { it.event.id == id }
        if (index < 0) return@withLock
        val entry = entries[index]
        if (permanent) {
            entries.removeAt(index)
            deleteQuietly(keyFor(entry.seq))
        } else {
            val updated = entry.event.copy(attempts = entry.event.attempts + 1)
            entries[index] = entry.copy(event = updated)
            writeQuietly(entry.seq, updated)
        }
    }

    private suspend fun loadLocked() {
        if (loaded) return
        loaded = true

        val restored = mutableListOf<Entry>()
        var maxSeq = 0L
        for (key in listKeysQuietly()) {
            if (!key.startsWith(KEY_PREFIX)) continue
            val seq = key.removePrefix(KEY_PREFIX).toLongOrNull()
            if (seq == null) {
                deleteQuietly(key)
                continue
            }
            // Every key with our prefix counts towards the sequence, including ones we throw
            // away: reusing a dropped key would place a new event before older ones.
            if (seq > maxSeq) maxSeq = seq
            val raw = try {
                storage.read(key)
            } catch (e: Exception) {
                null
            }
            val event = raw?.let { parse(it) }
            if (event == null) {
                deleteQuietly(key)
                continue
            }
            restored.add(Entry(seq, event))
        }

        restored.sortBy { it.seq }
        entries.clear()
        restored.forEach { entries.addLast(it) }
        // Never reuse a sequence: memory order comes from insertion, disk order from the key.
        nextSeq = maxSeq + 1
        trimLocked()
    }

    private suspend fun trimLocked() {
        val nowMs = now()
        while (entries.isNotEmpty() && nowMs - entries.first().event.createdAtMs > maxAgeMs) {
            val dropped = entries.removeFirst()
            Logger.e("EventQueue", "dropping ${dropped.event.type} ${dropped.event.id}: older than ${maxAgeMs}ms")
            deleteQuietly(keyFor(dropped.seq))
        }
        while (entries.size > maxEvents) {
            val dropped = entries.removeFirst()
            Logger.e("EventQueue", "dropping ${dropped.event.type} ${dropped.event.id}: queue over $maxEvents events")
            deleteQuietly(keyFor(dropped.seq))
        }
    }

    private fun keyFor(seq: Long): String = KEY_PREFIX + seq.toString().padStart(SEQ_DIGITS, '0')

    private fun parse(raw: String): ReportEvent? = try {
        val obj = Serializer.json.parseToJsonElement(raw) as? JsonObject
        val id = obj?.get("id")?.jsonPrimitive?.contentOrNull
        val type = obj?.get("type")?.jsonPrimitive?.contentOrNull
            ?.let { name -> ReportEventType.entries.firstOrNull { it.name == name } }
        if (obj == null || id == null || type == null) {
            null
        } else {
            ReportEvent(
                id = id,
                type = type,
                payload = (obj["payload"] as? JsonObject)?.entries?.mapNotNull { (key, value) ->
                    (value as? JsonPrimitive)?.contentOrNull?.let { key to it }
                }?.toMap() ?: emptyMap(),
                createdAtMs = obj["createdAtMs"]?.jsonPrimitive?.longOrNull ?: 0L,
                attempts = obj["attempts"]?.jsonPrimitive?.intOrNull ?: 0
            )
        }
    } catch (e: Exception) {
        Logger.e("EventQueue", "unreadable queue entry, dropping: ${e.message}")
        null
    }

    private fun encode(event: ReportEvent): String = buildJsonObject {
        put("id", event.id)
        put("type", event.type.name)
        put("createdAtMs", event.createdAtMs)
        put("attempts", event.attempts)
        putJsonObject("payload") { event.payload.forEach { (key, value) -> put(key, value) } }
    }.toString()

    private suspend fun writeQuietly(seq: Long, event: ReportEvent) {
        try {
            storage.write(keyFor(seq), encode(event))
        } catch (e: Exception) {
            Logger.e("EventQueue", "queue write failed: ${e.message}")
        }
    }

    private suspend fun deleteQuietly(key: String) {
        try {
            storage.delete(key)
        } catch (e: Exception) {
        }
    }

    private suspend fun listKeysQuietly(): List<String> = try {
        storage.listKeys()
    } catch (e: Exception) {
        emptyList()
    }
}
